import { PermissionInfo } from 'types/rolePermission'
import useUserSession from './useUserSession'
import { useMemo } from 'react'

const codeCanWriteDraftInvoices = 'portal-write-draft-invoices'
const codeCanSubmitDraftInvoices = 'portal-submit-draft-invoices'
const codeCanReadDraftInvoices = 'portal-read-draft-invoices'
const codeCanDeleteDraftInvoices = 'portal-delete-draft-invoices'
const codeBrightBoltAccess = 'portal-brightbolt-access'
const codeCanWriteDebtorRelationship = 'portal-write-debtor-relationship'
const codeCanWriteDebtorCreation = 'portal-write-debtor-creation'
const codeCanReadInvoices = 'portal-read-invoices'
const codeCanWriteInvoices = 'portal-write-invoices'
const codeCanWriteCsvInvoices = 'portal-write-csv-invoices'
const codeCanWriteInvoiceDocuments = 'portal-write-invoice-documents'
const codeCanDisplayInvoiceUploadSection = 'portal-display-upload-invoice-documents'
const codeCanAccessDebtors = 'portal-read-debtors'
const codeCanAcceesReports = 'portal-read-reports'
const codeCanReadDashboard = 'portal-read-dashboard'
const codeCanAccessNotConnectedDebtors = 'can-access-not-connected-debtors'

export function hasPermission(permissionsToCheck: string[], userPermissions: PermissionInfo[] = []) {
  const pList = userPermissions.map((p) => p.code)
  return permissionsToCheck.every((permission) => pList.includes(permission))
}

const usePermissions = () => {
  const { permissions, isClient, isBroker } = useUserSession()
  const isPermissionsLoaded = typeof permissions !== 'undefined'

  const canReadInvoices = useMemo(() => hasPermission([codeCanReadInvoices], permissions), [permissions])
  const canClientCreateInvoicesBlade = useMemo(() => hasPermission([codeCanWriteInvoices], permissions), [permissions])
  const canBrokerCreateInvoicesBlade = useMemo(() => hasPermission([codeCanWriteInvoices], permissions), [permissions])
  const canCreateInvoicesBlade =
    (isClient && canClientCreateInvoicesBlade) || (isBroker && canBrokerCreateInvoicesBlade)
  const canImportInvoices =
    useMemo(() => hasPermission([codeCanWriteCsvInvoices], permissions), [permissions]) && isClient
  const canReadDraftInvoices = useMemo(() => hasPermission([codeCanReadDraftInvoices], permissions), [permissions])
  const canUploadInvoiceDocuments = useMemo(
    () => hasPermission([codeCanWriteInvoiceDocuments], permissions),
    [permissions]
  )
  const canDisplayInvoiceUploadSection = useMemo(
    () => hasPermission([codeCanDisplayInvoiceUploadSection], permissions),
    [permissions]
  )
  const canAccessDebtors = useMemo(() => hasPermission([codeCanAccessDebtors], permissions), [permissions])
  const canRequestDebtorCreation = useMemo(
    () => hasPermission([codeCanWriteDebtorCreation], permissions),
    [permissions]
  )
  const canRequestDebtorRelationship = useMemo(
    () => hasPermission([codeCanWriteDebtorRelationship], permissions),
    [permissions]
  )
  const canUploadDocumentToCreateDraftInvoice =
    useMemo(
      () => hasPermission([codeCanWriteDraftInvoices, codeCanSubmitDraftInvoices, codeBrightBoltAccess], permissions),
      [permissions]
    ) && isClient
  const canDeleteDraftInvoices = useMemo(() => hasPermission([codeCanDeleteDraftInvoices], permissions), [permissions])
  const canEditDraftInvoices = useMemo(() => hasPermission([codeCanSubmitDraftInvoices], permissions), [permissions])
  const canAccessReports = useMemo(() => hasPermission([codeCanAcceesReports], permissions), [permissions])
  const canReadDashboard = useMemo(() => hasPermission([codeCanReadDashboard], permissions), [permissions])
  const canAccessNotConnectedDebtors = useMemo(
    () => hasPermission([codeCanAccessNotConnectedDebtors], permissions),
    [permissions]
  )

  return {
    canReadInvoices,
    canCreateInvoicesBlade,
    canImportInvoices,
    canReadDraftInvoices,
    canUploadInvoiceDocuments,
    canDisplayInvoiceUploadSection,
    canAccessDebtors,
    canRequestDebtorCreation,
    canRequestDebtorRelationship,
    canUploadDocumentToCreateDraftInvoice,
    canDeleteDraftInvoices,
    canEditDraftInvoices,
    canAccessReports,
    canReadDashboard,
    isPermissionsLoaded,
    canAccessNotConnectedDebtors,
  }
}

export default usePermissions
